import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CarruselCursos.css';
import CourseImageFill from './CourseImageFill';

const CarruselCursos = ({ cursos }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPorPagina, setItemsPorPagina] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayRef = useRef();

  // Configuración de responsividad
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1400) setItemsPorPagina(3);
      else if (window.innerWidth >= 1024) setItemsPorPagina(3);
      else if (window.innerWidth >= 768) setItemsPorPagina(2);
      else setItemsPorPagina(1);
    };

    handleResize(); // Inicial
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Si hay items "sobrantes" al final, limitamos el índice máximo para no mostrar vacío
  const maxIndex = Math.max(0, cursos.length - itemsPorPagina);

  useEffect(() => {
    setCurrentIndex(prev => Math.min(prev, maxIndex));
  }, [maxIndex]);

  // Funciones de navegación
  const next = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prev = () => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goTo = (index) => {
    setCurrentIndex(Math.min(Math.max(0, index), maxIndex));
  };

  // Autoplay
  useEffect(() => {
    autoPlayRef.current = next;
  });

  useEffect(() => {
    const play = () => {
      autoPlayRef.current();
    };

    let interval;
    if (!isHovered && cursos.length > itemsPorPagina) {
      interval = setInterval(play, 4000); // 4 segundos
    }
    return () => clearInterval(interval);
  }, [isHovered, cursos.length, itemsPorPagina]);

  // Manejo Táctil (Swipe)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsHovered(true); // Pausar al tocar
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) next();
    if (isRightSwipe) prev();
    
    setIsHovered(false); // Reanudar al soltar
  };

  // Renderizado condicional si no hay cursos
  if (!cursos || cursos.length === 0) return null;

  return (
    <div 
      className="carousel-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-label="Carrusel de cursos destacados"
    >
      <div className="carousel-wrapper">
        {/* Botón Anterior */}
        {cursos.length > itemsPorPagina && (
          <button onClick={prev} className="carousel-arrow left-arrow" aria-label="Curso anterior">
            &lt;
          </button>
        )}

        {/* Contenedor de items */}
        <div className="carousel-content-wrapper">
          <div 
            className="carousel-content"
            style={{ 
              transform: `translateX(-${currentIndex * (100 / itemsPorPagina)}%)`,
              '--items-per-page': itemsPorPagina
            }}
          >
            {cursos.map((curso) => (
              <div 
                key={curso._id} 
                className="carousel-card-container"
                style={{ width: `${100 / itemsPorPagina}%` }}
              >
                <div 
                  className="card-curso-interactivo"
                  onClick={() => navigate(`/curso/${curso._id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' '){
                      e.preventDefault();
                      navigate(`/curso/${curso._id}`);
                    }
                  }}
                >
                  <div className="card-image-wrapper">
                    <CourseImageFill
                      src={curso.imagen || ''}
                      alt={curso.titulo}
                      loading="lazy"
                    />
                  </div>
                  <div className="card-content">
                    <h3>{curso.titulo}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botón Siguiente */}
        {cursos.length > itemsPorPagina && (
          <button onClick={next} className="carousel-arrow right-arrow" aria-label="Siguiente curso">
            &gt;
          </button>
        )}
      </div>

      {/* Indicadores (Dots) */}
      {cursos.length > itemsPorPagina && (
        <div className="carousel-indicators">
          {Array.from({ length: cursos.length - itemsPorPagina + 1 }).map((_, index) => {
             const skipOdd = cursos.length > 20;
            if (skipOdd && index % 2 !== 0) return null;
            
            // When skipping odd indices, determine if this dot should be active
            const isActive = skipOdd
              ? (currentIndex === index || currentIndex === index +1)
              : currentIndex === index;

             return (
              <button
                key={index}
                className={`indicator ${isActive ? 'active' : ''}`}
                onClick={() => goTo(index)}
                aria-label={`Ir a la posición ${index + 1}`}
                aria-current={isActive ? 'true' : 'false'}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CarruselCursos;
